import { HttpTransportType, HubConnection, HubConnectionBuilder } from "@microsoft/signalr";

type PendingHandler = { eventName: string; callback: (data: string) => void };

export const signalRService = {
  connection: null as HubConnection | null,
  isConnecting: false,
  _pendingHandlers: [] as PendingHandler[],

  startConnection: ({
    accessToken,
    maxRetries = 3,
    retryInterval = 3000,
  }: {
    accessToken: string;
    maxRetries?: number;
    retryInterval?: number;
  }) => {
    if (signalRService.isConnecting) {
      return;
    }

    signalRService.isConnecting = true;

    let retries = 0;

    const connect = () => {
      if (!signalRService.connection) {
        signalRService.connection = new HubConnectionBuilder()
          .withUrl(
            `${import.meta.env.VITE_API_URL || "https://api-qas.talk2buy.com.br"}/hubs/message`,
            {
              skipNegotiation: true,
              transport: HttpTransportType.WebSockets,
              accessTokenFactory: () => {
                return accessToken;
              },
            }
          )
          .withAutomaticReconnect([1000, 3000, 5000, 10000])
          .build();
        signalRService._attachPendingHandlers();
      }

      if (!["Connected", "Connecting", "Disconnecting"].includes(signalRService.connection.state)) {
        signalRService.connection
          .start()
          .then(() => {
            retries = 0;
          })
          .catch(() => {
            if (retries < maxRetries) {
              retries++;
              setTimeout(connect, retryInterval);
            }
          })
          .finally(() => {
            signalRService.isConnecting = false;
          });
      } else {
        signalRService.isConnecting = false;
      }
    };
    connect();
  },

  _attachPendingHandlers() {
    const conn = signalRService.connection;
    if (!conn || signalRService._pendingHandlers.length === 0) return;
    for (const { eventName, callback } of signalRService._pendingHandlers) {
      conn.on(eventName, callback);
    }
    signalRService._pendingHandlers = [];
  },

  on: (eventName: string, callback: (data: string) => void) => {
    if (signalRService.connection) {
      signalRService.connection.on(eventName, callback);
    } else {
      signalRService._pendingHandlers.push({ eventName, callback });
    }
  },

  off: (eventName: string, callback: (data: string) => void) => {
    if (signalRService.connection) {
      signalRService.connection.off(eventName, callback);
    }
    const idx = signalRService._pendingHandlers.findIndex(
      (h) => h.eventName === eventName && h.callback === callback
    );
    if (idx !== -1) signalRService._pendingHandlers.splice(idx, 1);
  },
};
